import { Box, Flex, Icon } from '@chakra-ui/react';
import { Lightning01 } from '@kula-ai/icons/dual-tone/Lightning01';
import { SectionLabel } from '@kula-ai/components';
import { KText } from '@/ui-library/components/text';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import type { WorkflowRule } from '@/pages/ats-job-creation/tabs/interview-plan/stage/automation/api/workflowRule.types';
import { getActivityKindIcon } from './activityKindIcon';
import { formatAutomationTiming } from './formatAutomationTiming';
import type { StoppingAction } from './deriveCurrentStageStopping';
import { MOVE_WITH_AUTOMATION_STRINGS } from './moveWithAutomation.strings';

const STR = MOVE_WITH_AUTOMATION_STRINGS;

type TimingBadgeProps = { label: string; dataTestId?: string };

const TimingBadge = ({ label, dataTestId }: TimingBadgeProps) => (
  // Cap the badge at the full row (≈ modal) width so a long timing string
  // (e.g. "After <long activity> scorecards are submitted") truncates with an
  // ellipsis + tooltip instead of stretching the modal.
  <Flex
    alignItems="center"
    gap="4px"
    bg="primary-50"
    border="1px solid"
    borderColor="primary-100"
    borderRadius="6px"
    px="6px"
    py="2px"
    minW={0}
    maxW="100%"
    data-test-id={dataTestId}
  >
    <Icon
      as={Lightning01}
      w="12px"
      h="12px"
      color="primary-600"
      flexShrink={0}
    />
    <TruncatedTextWithTooltip
      text={label}
      size="xs-medium"
      color="primary-700"
      minW={0}
    />
  </Flex>
);

type ActivityRowProps = {
  kind: string;
  label: string;
  badge: React.ReactNode;
  dataTestId?: string;
};

const ActivityRow = ({ kind, label, badge, dataTestId }: ActivityRowProps) => {
  const KindIcon = getActivityKindIcon(kind);
  return (
    <Flex
      alignItems="center"
      gap="8px"
      flexWrap="wrap"
      data-test-id={dataTestId}
    >
      {/* Cap the label at the full row (≈ modal) width so a long activity
          name truncates with an ellipsis + tooltip rather than overflowing. */}
      <Flex alignItems="center" gap="6px" minW={0} maxW="100%">
        <Icon
          as={KindIcon}
          w="14px"
          h="14px"
          color="secondary-600"
          flexShrink={0}
        />
        <TruncatedTextWithTooltip
          text={label}
          size="sm-medium"
          color="secondary-700"
          minW={0}
        />
      </Flex>
      {badge}
    </Flex>
  );
};

export type AutomationPreviewBodyProps = {
  targetStageName: string;
  targetRule: WorkflowRule | null;
  currentStageName?: string;
  currentStageStoppingActions?: StoppingAction[];
  dataTestId?: string;
};

export const AutomationPreviewBody = ({
  targetStageName,
  targetRule,
  currentStageName,
  currentStageStoppingActions = [],
  dataTestId = 'automation-preview',
}: AutomationPreviewBodyProps) => {
  const targetActions = targetRule?.actions ?? [];
  const showCurrentStage =
    Boolean(currentStageName) && currentStageStoppingActions.length > 0;
  // Hide the target block when the target stage has no automation — without
  // this its stage-name header would render with no activity rows beneath
  // it. Lets a caller show the current-stage cancel note alone (e.g.
  // transferring into a stage that has no automation).
  const showTargetStage = targetActions.length > 0;

  // Nothing to preview on either side — render nothing so the "Automation"
  // label never appears orphaned. Callers generally gate on this too.
  if (!showCurrentStage && !showTargetStage) return null;

  return (
    <Box data-test-id={dataTestId} py="4px">
      <Flex mb="12px" data-test-id={`${dataTestId}-label`}>
        <SectionLabel
          before={
            <Icon as={Lightning01} w="14px" h="14px" color="primary-600" />
          }
        >
          {STR.modal.automationSectionLabel}
        </SectionLabel>
      </Flex>

      {showCurrentStage && (
        <Flex
          direction="column"
          gap="8px"
          mb="12px"
          data-test-id={`${dataTestId}-current-stage`}
        >
          <KText
            size="sm-medium"
            color="secondary-900"
            data-test-id={`${dataTestId}-current-stage-name`}
          >
            {currentStageName}
          </KText>
          <KText
            size="sm-regular"
            color="secondary-700"
            data-test-id={`${dataTestId}-current-stage-note`}
          >
            {STR.currentStage.allCanceled}
          </KText>
        </Flex>
      )}

      {showTargetStage && (
        <Flex
          direction="column"
          gap="8px"
          data-test-id={`${dataTestId}-target-stage`}
        >
          <KText
            size="sm-medium"
            color="secondary-900"
            data-test-id={`${dataTestId}-target-stage-name`}
          >
            {targetStageName}
          </KText>
          {targetActions.map((action) => {
            const kind = action.target?.kind ?? '';
            const label = action.target?.label ?? kind;
            const timing = formatAutomationTiming(action, targetActions);
            return (
              <ActivityRow
                key={`target-${action.id}`}
                kind={kind}
                label={label}
                dataTestId={`${dataTestId}-target-activity`}
                badge={
                  <TimingBadge
                    label={timing}
                    dataTestId={`${dataTestId}-target-badge`}
                  />
                }
              />
            );
          })}
        </Flex>
      )}
    </Box>
  );
};
